import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ShoppingCart, ArrowRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/contexts/CartContext";
import { Template } from "@/types/templates";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";

const TemplatePreview = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToCart, isInCart } = useCart();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const { data: packetCategory } = await supabase
          .from("template_categories")
          .select("id")
          .eq("slug", "packet-review")
          .maybeSingle();

        const query = supabase
          .from("products")
          .select("*")
          .eq("is_active", true)
          .eq("is_bundle", false)
          .order("display_order", { ascending: true })
          .limit(8);

        if (packetCategory?.id) {
          query.neq("category_id", packetCategory.id);
        }

        const { data, error } = await query;

        if (error) throw error;
        setTemplates((data as Template[]) || []);
      } catch (err) {
        console.error("Error fetching templates:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleAddToCart = (template: Template) => {
    addToCart(template);
    toast.success(`${template.name} added to cart!`);
  };

  if (loading) {
    return (
      <section id="templates-section" className="py-12 lg:py-16 bg-background">
        <div className="container-custom flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  return (
    <section id="templates-section" className="py-12 lg:py-16 bg-background">
      <div className="container-custom" ref={ref}>
        <div className={cn(
          "text-center mb-10 transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Popular <span className="text-gradient">Templates</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Need just one specific letter? Browse our most popular dispute templates.
          </p>
        </div>

        <div className={cn(
          "grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-8 stagger-children",
          isVisible && "visible"
        )}>
          {templates.map((template, index) => {
            const inCart = isInCart(template.id);
            return (
              <div
                key={template.id}
                className="bg-card rounded-xl shadow-custom-sm border border-border/50 overflow-hidden flex flex-col hover:shadow-custom-md transition-all duration-300 tilt-card group"
              >
                {/* Image with zoom effect */}
                {template.image_url && (
                  <div className="aspect-[4/3] bg-white overflow-hidden">
                    <img
                      src={template.image_url}
                      alt={template.name}
                      className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-semibold text-foreground mb-1 line-clamp-2 text-sm">
                    {template.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3 line-clamp-2 flex-1">
                    {template.short_description || template.description}
                  </p>

                  {/* Price */}
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-lg font-bold text-foreground number-highlight">
                      ${template.price}
                    </span>
                    {template.original_price &&
                      template.original_price > template.price && (
                        <span className="text-xs text-muted-foreground line-through">
                          ${template.original_price}
                        </span>
                      )}
                  </div>

                  {/* Actions with slide-up effect */}
                  <div className="space-y-2">
                    <Button
                      variant={inCart ? "outline" : "default"}
                      size="sm"
                      className={cn(
                        "w-full transition-all duration-300",
                        !inCart && "group-hover:bg-primary-dark"
                      )}
                      onClick={() => handleAddToCart(template)}
                      disabled={inCart}
                    >
                      {inCart ? (
                        <>
                          <Check className="w-4 h-4 mr-1 animate-check-in" />
                          In Cart
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Add to Cart
                        </>
                      )}
                    </Button>
                    {template.slug && (
                      <Link to={`/templates/${template.slug}`} className="block">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-muted-foreground hover:text-primary"
                        >
                          View Details
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* View all and helper */}
        <div className={cn(
          "text-center space-y-4 transition-all duration-700 delay-500",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          <Link to="/templates">
            <Button variant="outline" size="lg" className="group">
              Browse All Templates
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground">
            Not sure which one?{" "}
            <button
              onClick={() =>
                document
                  .querySelector("#kit-section")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="text-primary hover:underline font-medium"
            >
              Start with the Complete Kit
            </button>{" "}
            — it includes all templates.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TemplatePreview;
